import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface GuardOptions {
    onSave?: () => Promise<boolean | void> | boolean | void;
    message?: string;
    title?: string;
}

export interface NavigationGuardContextType {
    setDirtyState: (isDirty: boolean, options?: GuardOptions) => void;
    safeNavigate: (to: string | number, options?: any) => void;
    isDirty: boolean;
}

const NavigationGuardContext = createContext<NavigationGuardContextType>({
    setDirtyState: () => {},
    safeNavigate: () => {},
    isDirty: false,
});

export const NavigationGuardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [isDirty, setIsDirty] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pendingTarget, setPendingTarget] = useState<{ to: string | number; options?: any } | null>(null);

    const isDirtyRef = useRef(false);
    const optionsRef = useRef<GuardOptions>({});

    const setDirtyState = useCallback((dirty: boolean, opts?: GuardOptions) => {
        setIsDirty(dirty);
        isDirtyRef.current = dirty;
        if (opts) {
            optionsRef.current = opts;
        } else if (!dirty) {
            optionsRef.current = {};
        }
    }, []);

    // 1. Browser unload / refresh / change URL protection
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isDirtyRef.current) {
                e.preventDefault();
                e.returnValue = t('lessons.unsaved_changes_warning', 'Thay đổi của bạn chưa được lưu, bạn có muốn lưu lại trước khi thoát ra không?');
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [t]);

    // 2. Browser Back / Forward button protection (popstate)
    useEffect(() => {
        window.history.pushState({ guardActive: true }, '', window.location.href);

        const handlePopState = () => {
            if (isDirtyRef.current) {
                window.history.pushState({ guardActive: true }, '', window.location.href);
                setPendingTarget({ to: -1 });
                setConfirmOpen(true);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    const safeNavigate = useCallback((to: string | number, options?: any) => {
        if (isDirtyRef.current) {
            setPendingTarget({ to, options });
            setConfirmOpen(true);
        } else {
            if (typeof to === 'number') {
                navigate(to);
            } else {
                navigate(to, options);
            }
        }
    }, [navigate]);

    const handleConfirm = async (action: 'cancel' | 'discard' | 'save') => {
        if (action === 'cancel') {
            setConfirmOpen(false);
            setPendingTarget(null);
        } else if (action === 'discard') {
            setConfirmOpen(false);
            isDirtyRef.current = false;
            setIsDirty(false);
            const target = pendingTarget;
            setPendingTarget(null);
            if (target) {
                if (typeof target.to === 'number') {
                    navigate(target.to);
                } else {
                    navigate(target.to, target.options);
                }
            }
        } else if (action === 'save') {
            const currentPath = window.location.pathname;
            const onSave = optionsRef.current.onSave;
            if (onSave) {
                setSaving(true);
                try {
                    const result = await onSave();
                    if (result === false) {
                        setSaving(false);
                        return;
                    }
                } catch (err) {
                    console.error('Save failed in navigation guard:', err);
                    setSaving(false);
                    return;
                } finally {
                    setSaving(false);
                }
            }
            setConfirmOpen(false);
            isDirtyRef.current = false;
            setIsDirty(false);
            const target = pendingTarget;
            setPendingTarget(null);
            // Only perform pending navigation if onSave didn't already navigate to another URL
            if (target && window.location.pathname === currentPath) {
                if (typeof target.to === 'number') {
                    navigate(target.to);
                } else {
                    navigate(target.to, target.options);
                }
            }
        }
    };

    return (
        <NavigationGuardContext.Provider value={{ setDirtyState, safeNavigate, isDirty }}>
            {children}
            <Dialog
                open={confirmOpen}
                onClose={() => !saving && handleConfirm('cancel')}
                PaperProps={{ sx: { borderRadius: 3, p: 1, maxWidth: 450 } }}
            >
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {optionsRef.current.title || t('lessons.unsaved_changes_title', 'Thay đổi chưa được lưu')}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" color="text.secondary">
                        {optionsRef.current.message || t('lessons.unsaved_changes_warning', 'Thay đổi của bạn chưa được lưu, bạn có muốn lưu lại trước khi thoát ra không?')}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
                    <Button onClick={() => handleConfirm('cancel')} disabled={saving} sx={{ borderRadius: 2 }}>
                        {t('general.cancel', 'Hủy')}
                    </Button>
                    <Button onClick={() => handleConfirm('discard')} disabled={saving} color="error" sx={{ borderRadius: 2 }}>
                        {t('lessons.exit_without_saving', 'Thoát không lưu')}
                    </Button>
                    {Boolean(optionsRef.current.onSave) && (
                        <Button onClick={() => handleConfirm('save')} disabled={saving} variant="contained" color="primary" sx={{ borderRadius: 2 }}>
                            {saving ? <CircularProgress size={20} color="inherit" /> : t('lessons.save_and_exit', 'Lưu lại')}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </NavigationGuardContext.Provider>
    );
};

export const useNavigationGuard = () => {
    return useContext(NavigationGuardContext);
};
